import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";

import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import UserProfile from "./UserProfile";
import EditProfile from "./EditProfile";
import AdminUserManagement from "./AdminUserManagement";
import BrokerManagement from "./BrokerManagement";
import InstrumentList from "./InstrumentList";
import TradesOrders from "./TradesOrders";
import ProfitPlans from "./ProfitPlans";

import { useSocket } from "../../context/WebSocketContext";

const drawerWidth = 0;

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showUserManagement, setShowUserManagement] = useState(false);
    const [showBrokerManagement, setBrokerManagement] = useState(false);
    const [showInstrument, setInstrument] = useState(false);
    const [showTradesOrders, setTradesOrders] = useState(false);
    const [showProfitPlans, setProfitPlans] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const token = localStorage.getItem("token");

    const { status: wsStatus } = useSocket();

    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/login");
                return;
            }
            try {
                const res = await axios.get(`${API_BASE_URL}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(res.data);
            } catch (err) {
                setError("Failed to fetch user info");
                localStorage.removeItem("token");
                navigate("/login");
            }
        };
        fetchUser();
    }, [navigate, token]);

    if (error)
        return (
            <Box maxWidth={400} mx="auto" mt={8} p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );

    if (!user)
        return (
            <Box
                maxWidth={400}
                mx="auto"
                mt={8}
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
            >
                <CircularProgress />
            </Box>
        );

    return (
        <Box sx={{ display: "flex" }}>
            <Sidebar
                userRole={user.role}
                onProfitPlans={() => { setProfitPlans(true); setIsEditing(false); setShowUserManagement(false); setBrokerManagement(false); setInstrument(false); setTradesOrders(false); }}
                onDashboard={() => {
                    setIsEditing(false);
                    setShowUserManagement(false);
                    setBrokerManagement(false);
                    setInstrument(false);
                    setTradesOrders(false);
                    setProfitPlans(false);
                }}
                onInstrument={() => {
                    setInstrument(true);
                    setBrokerManagement(false);
                    setShowUserManagement(false);
                    setIsEditing(false);
                    setTradesOrders(false);
                    setProfitPlans(false);
                }}
                onUserManagement={() => {
                    setShowUserManagement(true);
                    setIsEditing(false);
                    setBrokerManagement(false);
                    setInstrument(false);
                    setTradesOrders(false);
                    setProfitPlans(false);
                }}
                onBrokerManagement={() => {
                    setBrokerManagement(true);
                    setShowUserManagement(false);
                    setIsEditing(false);
                    setInstrument(false);
                    setTradesOrders(false);
                    setProfitPlans(false);
                }}
                onTradesOrders={() => {
                    setBrokerManagement(false);
                    setShowUserManagement(false);
                    setIsEditing(false);
                    setInstrument(false);
                    setTradesOrders(true);
                    setProfitPlans(false);
                }}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: "background.default",
                    p: 3,
                    ml: `${drawerWidth}px`,
                    minHeight: "100vh",
                    position: "relative",
                }}
            >
                <Topbar
                    user={user}
                    wsStatus={wsStatus}
                    onEditProfile={() => setIsEditing(true)}
                    onLogout={() => {
                        localStorage.removeItem("token");
                        navigate("/login");
                    }}
                />

                {showProfitPlans && !isEditing ? (
                    <ProfitPlans />
                ) : showUserManagement ? (
                    <AdminUserManagement />
                ) : isEditing ? (
                    <EditProfile user={user} onCancel={() => setIsEditing(false)} onUserUpdate={setUser}/>
                ) : showBrokerManagement ? (
                    <BrokerManagement />
                ) : showInstrument ? (
                    <InstrumentList user={user} />
                ) : showTradesOrders ? (
                    <TradesOrders user={user} />
                ) : (
                    <UserProfile user={user} wsStatus={wsStatus} />
                )}
            </Box>
        </Box>
    );
};

export default Dashboard;
